import { Identity } from '../account'
import { Category } from '../transaction/category'
import { PaymentMethod, TransactionStatus, VatCode } from '../transaction/transaction'

export type Charter = {
  id: string
  awxConnectedAccountId: string
  name: string
  startDate: string
  endDate: string
  delivery: string
  reDelivery: string
  charterFeeCents: number
  apaCents: number
  vat: string
  currencyCode: string
  contactFullName: string
  contactEmail: string
  contactMobileCountryCode: string
  contactMobileNumber: string
  createdAt: string
  updatedAt: string
  createdBy: string
  status: CharterStatus
}

export enum CharterStatus {
  // the charter is upcoming and not yet started, anyone can submit a transaction (manual or digital)
  UPCOMING = 'UPCOMING',
  // the charter is in progress, anyone can submit a transaction (manual or digital)
  IN_PROGRESS = 'IN_PROGRESS',
  // the charter is in signing off, only the admin/owner can submit a transaction (manual)
  SIGNING_OFF = 'SIGNING_OFF',
  // the charter is completed, no more transactions can be submitted
  COMPLETED = 'COMPLETED',
  // the charter is cancelled, no more transactions can be submitted
  CANCELLED = 'CANCELLED',
}

export type CharterSummary = Omit<Charter, 'createdBy'> & {
  createdBy: Identity
}

export type CreateCharterRequest = {
  /**
   * The name of the charter
   */
  name: string
  /**
   * The start date (ISO 8601) string of the charter
   */
  startDate: string
  /**
   * The end date (ISO 8601) string of the charter
   */
  endDate: string
  /**
   * The delivery location of the charter
   */
  delivery: string
  /**
   * The re-delivery location of the charter
   */
  reDelivery: string
  /**
   * The charter fee in cents
   */
  charterFeeCents: number
  /**
   * The APA fee in cents
   */
  apaCents: number
  /**
   * The VAT rate (e.g. '10')
   */
  vat: string
  /**
   * The currency code (e.g. AUD)
   */
  currencyCode: string
  /**
   * The full name of the contact
   */
  contactFullName: string
  /**
   * The email of the contact
   */
  contactEmail: string
  /**
   * The mobile country code of the contact (e.g. +61)
   */
  contactMobileCountryCode: string
  /**
   * The mobile number of the contact
   */
  contactMobileNumber: string
}

export type UpdateCharterRequest = Omit<CreateCharterRequest, 'currencyCode'> & {
  status: CharterStatus
  // we want to override currencyCode as being optional on updates
  currencyCode?: string
}

export type SearchChartersRequest = {
  /**
   * Text search query for charter name (case-insensitive)
   */
  query?: string
  /**
   * Filter by charter status(es)
   */
  status?: CharterStatus[]
  /**
   * Filter charters with start date >= this date (ISO 8601)
   */
  startDateFrom?: string
  /**
   * Filter charters with start date <= this date (ISO 8601)
   */
  startDateTo?: string
  /**
   * Filter charters with end date >= this date (ISO 8601)
   */
  endDateFrom?: string
  /**
   * Filter charters with end date <= this date (ISO 8601)
   */
  endDateTo?: string
  /**
   * Filter charters with charter fee >= this amount (in cents)
   */
  charterFeeMin?: number
  /**
   * Filter charters with charter fee <= this amount (in cents)
   */
  charterFeeMax?: number
  /**
   * Filter charters with APA fee >= this amount (in cents)
   */
  apaMin?: number
  /**
   * Filter charters with APA fee <= this amount (in cents)
   */
  apaMax?: number
  /**
   * Number of records to return (default: 10, max: 1000)
   */
  take?: number
  /**
   * Number of records to skip for pagination
   */
  skip?: number
  /**
   * Field to sort by (default: updatedAt)
   */
  sortBy?: 'updatedAt' | 'createdAt' | 'startDate' | 'endDate' | 'name'
  /**
   * Sort order (default: desc)
   */
  sortOrder?: 'asc' | 'desc'
}

export type CreateCharterAttachmentRequest = {
  name: string
  mimeType: string
  charterId: string
  uploadedBy: string
}

export type CharterAttachment = {
  id: string
  charterId: string
  name: string
  mimeType: string
  uploadedAt: string
  path: string
  uploadedBy: string
}

export type CharterAttachmentPayload = Omit<CharterAttachment, 'uploadedBy' | 'path' | 'charterId'> & {
  url: string
}

export type CharterAttachmentBody = {
  name: string
  mimeType: string
}

export type PreSignedAttachmentUploadBody = {
  path: string
}

export type CharterTransactions = {
  id: string
  apaAmountCents: bigint
  transactions: {
    amountCents: bigint
    payableVatAmountCents: bigint
    status: TransactionStatus
  }[]
}

export type CharterStatsPayload = {
  id: string
  apaSpentCents: number
  apaRemainingCents: number
  payableVatAmountCents: number
  nonReviewedTransactionsCount: number
}

/**
 * Represents a type that contains all the info required to generate a charter ledger
 */
export type CharterLedger = {
  // charter with picked fields only
  charter: Pick<Charter, 'id' | 'name' | 'startDate' | 'endDate' | 'status' | 'apaCents' | 'charterFeeCents' | 'currencyCode' | 'vat'>
  // a summary of all the expenses
  summary: CharterLedgerSummary
  // a list of ordered transactions from the newest to the oldest 
  transactions: CharterLedgerTransaction[]
}

export type CharterLedgerSummary = {
  // total spend for each category, including other (uncategorized)
  totalByCategories: {
    [categoryId: string]: {
      totalAmountCents: number
    }
  }
  categories: Omit<Category, 'organizationId'>[]
  // the total payable vat amount for all transactions
  totalPayableVatAmountCents: number
  // what's left in the APA
  balanceCents: number
}

export type CharterLedgerTransaction = {
  // the transaction id
  id: string
  // the date when the transaction occurred
  date: string
  // the transaction status
  status: TransactionStatus
  // the transaction category
  category?: Omit<Category, 'organizationId'>
  // any comment on the transaction
  comments?: string
  // the payment method used for the transaction
  paymentMethod: PaymentMethod
  // full name of the person who submitted the transaction
  spenderFullName: string
  // the VAT code applied to the transaction
  vatCode?: VatCode
  /**
   * The VAT rate applied to the transaction (falls back to the charter vat rate when not provided)
   */
  vat?: string
  // the amount in cents of the transaction
  amountCents: number
  // the payable VAT amount in cents for this transaction
  payableVatAmountCents: number
  // the carrying apa balance
  carryingAPABalanceCents: number
}
